/*
================================================================================
                    WPQ GENERAL - BUSINESS LOGIC
================================================================================

PURPOSE: General Information tab - welder details, processes, base metals

WI INTEGRATION FIELDS (Used in API):
• WPQ_WelderId → Welder identification for slip generation
• WPQ_WelderName → Welder name display in WI slip
• WPQ_Process_Category → GSI indexing for welder queries
• wld_createdon → Last weld date tracking and 178-day validity
• WPQ_Welder_ExpiresOn → 178-day qualification validity check
• WPQ_BM_WTBM_Pno → P-Number matching with WPS base metals
• WPQ_BMT_Pipe_Diameter_RangeQ_Overlay → Pipe diameter qualification range
• WPQ_BMT_Plate_thickness_RangeQ_Overlay → Plate thickness qualification range
• WI_WPQ_610_Positions → Welding positions qualified for >610mm diameter
• WI_WPQ_73to610_Positions → Welding positions qualified for 73-610mm diameter
• WI_WPQ_73_Positions → Welding positions qualified for <73mm diameter
• WI_WPQ_FM_Arc_transfermode → Arc transfer mode matching with WPS
• WI_WPQ_FM_Product_Form → Filler metal product form validation
• WI_WPQ_FM_Current_Polarity → Current polarity compatibility check
• WI_WPQ_FM_F_No → F-Number matching for filler metal compatibility
• WI_WPQ_FM_Consumable_Insert → Consumable insert requirement (Groove only)
• WI_WPQ_SlipGenerationStatus → Tracks if WPQ used in WI slip generation
• WPSH_Status → Must be 'Approved' for WI slip eligibility
• wld_process_status → Must be 'Active' for current qualification
• wld_WPSType → Joint type matching (Groove/Overlay/Tubesheet)

REPORT FIELDS:
• WQC_Process/Type/Pcat → Pipe-delimited lists for WCR reports
• Process1/2/3 → Individual process fields
• WPQ_Process_Category → GSI key for WI welder selection queries

KEY RULES:
• 178-day validity: WPQ_Welder_ExpiresOn = wld_createdon + 178 days
• Date sync: WPQ_Date changes update ALL detail records
• Process category: "ProcessCategory#Type" for batch linking
• Welder ID locked after creation (Edit mode)

================================================================================
*/

import { Row, Col, Form } from "react-bootstrap";
import PropTypes from "prop-types";
import { useImperativeHandle, forwardRef, useState, useEffect } from "react";
import { company_name, Division, PlantLocation, WeldedCoupon } from "../utility/common/TransactionComponents/TransactionEntity"
import { Action, Status, MessageTypes } from "../utility/common/constants/CommonConstants"
import { SearchSelect } from "../utility/forms/inputs/search-select";
import { AddNewGrooveWPQ, getbyrecordno } from "../../Services/wpq_groove-services";
import { todayDate, maxDate } from "../utility/common/helpers/TodayDate";
import { getPNOActive } from "../../Services/pcategory-service";
import { getActiveProcess } from "../../Services/weldingprocess-services";
import { General_Single_Input_select, SingleInput } from "../utility/forms/inputs/InputsTransaction"
import { WPQVariableTable } from "./WPQObjects/WPQVariableTable";
import { WPQ_BaseMetalThichkness_Table } from "./WPQObjects/WPQ_BaseMetalThichkness_Table";
import { getActiveInsp } from "../../Services/inspectionagency-service";
import { getActiveWelder } from "../../Services/Welder-services";
import { AddNewDetail, getdetailsById, DeleteWPQ, getRecentLastWeldDate } from "../../Services/wpq_details-service";
import { ProcessTypeTable } from "../utility/common/TransactionComponents/ProcessTypeTable";
import { BaseMetalTable } from "./WPQObjects/BaseMetalTable";
import { DateInput } from "../utility/common/helpers/DateComponent";
import { DotLoader } from "react-spinners";
import { isAllowCustomSpecialCharacters, isAllowDashedSpecialCharacters } from "../../functions/validations";
import { validateWPQForm, validateWPQTempForm } from "./WPQ_ValidationConfig";
import { handleCount, emptyError, handleWPQHeaderChange, initializeWPQErrorState, handleWPQGeneralSubmit, handleWPQGeneralSubmitTemp } from "./WPQObjects/WPQCommonUtils";
import { calculateWPQExpiryDate } from "./WPQObjects/WPQ_common";
import { showWarning } from "../utility/ui/notifications/Toaster";

/*
 * WPQ_General Component - Handles general qualification information
 * 
 * COMPONENT WORKFLOW:
 * 1. Initialize form with welder, process, and inspection agency data
 * 2. Handle welder selection and auto-populate related fields
 * 3. Manage date synchronization between header and detail records
 * 4. Validate all required fields and business rules
 * 5. Process submission with header/detail record creation
 * 6. Handle approval workflow and status transitions
 */
const WPQ_General = forwardRef(
  (
    {
      headerObj,
      setheaderObj,
      detailObj,
      setdetailObj,
      normalstyle,
      errorstyle,
      blyestyle,
      WPStype,
      isReadonly,
      setReadonly,
      action,
      setAction,
    },
    ref
  ) => {
    WPQ_General.propTypes = {
      headerObj: PropTypes.array,
      setheaderObj: PropTypes.func,
      setdetailObj: PropTypes.func,
      detailObj: PropTypes.array,
      normalstyle: PropTypes.string,
      errorstyle: PropTypes.string,
      blyestyle: PropTypes.string,
      WPStype: PropTypes.string,
      isReadonly: PropTypes.bool,
      setReadonly: PropTypes.func,
      action: PropTypes.string,
      setAction: PropTypes.func,
    };
    let username = sessionStorage.getItem("UserName");
    const [ProcessActive, setProcessActive] = useState([]);
    const [PNoActive, setPNoActive] = useState([]);
    const [WelderActive, setWelderActive] = useState([]);
    const [inspecAgencyActive, setInspecAgencyActive] = useState([]);
    const [loading, setLoading] = useState(false);
    const [IsEdited, setIsEdited] = useState(false);

    const disableFields = isReadonly;
    console.log("headerObj", headerObj);
    const [error, setError] = useState(initializeWPQErrorState());

    const handleChange = (e) => {
      handleWPQHeaderChange(
        e,
        { setheaderObj, setError },
        { action, setIsEdited, setAction },
        error
      );

    };

    /*
    ================================================================================
                        LAST WELD DATE SYNCHRONIZATION LOGIC
    ================================================================================
    
    PURPOSE: Synchronizes welding date changes across all WPQ detail records
    
    BUSINESS RULES:
    1. Adding new record + WPQ date is today → last weld date = today's date
    2. Adding new record + WPQ date is not today → last weld date = recent date
    3. Copying record + WPQ date kept as today → last weld date = today's date
    4. Copying record + WPQ date changed to not today → last weld date = recent date
    
    • wld_createdon = Last weld date (DD-MM-YYYY format for details)
    • WPQ_Welder_ExpiresOn = wld_createdon + 178 days (YYYY-MM-DD format)
    • wld_process_status reset to "Active" for new qualification period

    WI INTEGRATION IMPACT:
    • WI slip generation uses wld_createdon for 178-day validity checks
    • Expired qualifications (>178 days) cannot be used in WI slips
    • Process category linking ensures all related processes get updated
    
    DATE FORMAT CONVERSION:
    • Header: YYYY-MM-DD (HTML date input format)
    • Details: DD-MM-YYYY (display/storage format)
    • Expiry: YYYY-MM-DD (ISO format for date calculations)
    
    SYNC TRIGGERS:
    • Manual date change in WPQ_General form
    • WI slip generation (updates last weld date to current)
    • Approval workflow (maintains date consistency)

    ================================================================================
    */

    const syncDatesWithWeldingDate = async (weldingDate) => {
      if (!weldingDate || detailObj.length === 0) return;

      // STEP 1: Convert YYYY-MM-DD (header format) to DD-MM-YYYY (detail format)
      const [year, month, day] = weldingDate.split("-");
      const detailDateFormat = `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
      const isToday = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).setHours(0,0,0,0) === new Date().setHours(0,0,0,0);
      
      console.log("📅 SYNC: Is today's date?", isToday);

      if (isToday) {
        // Rule 1 & 3: Use today's date
        const updatedDetails = detailObj.map(item => ({
          ...item,
          wld_createdon: detailDateFormat,
          WPQ_Welder_ExpiresOn: calculateWPQExpiryDate(detailDateFormat),
          wld_process_status: item.wld_process_status || "Active"
        }));
        setdetailObj(updatedDetails);
        return;
      }

      // Rule 2 & 4: Fetch recent dates
      const recentDatesCache = new Map();
      
      // Get unique process categories
      const uniqueCategories = [...new Set(
        detailObj
          .filter(item => item.WPQ_Process && item.WPQ_Type)
          .map(item => {
            const category = ProcessActive.find(p => p.wld_code === item.WPQ_Process)?.wld_p_category;
            return category ? `${category}#${item.WPQ_Type}` : null;
          })
          .filter(Boolean)
      )];

      // Fetch recent dates
      for (const processCategory of uniqueCategories) {
        try {
          const recentDate = await getRecentLastWeldDate({
            WPQ_WelderId: headerObj.WPQ_WelderId,
            WPQ_Process_Category : processCategory,
            WPSH_Status: "Approved"
          });
          if (recentDate) recentDatesCache.set(processCategory, recentDate);
        } catch (error) {
          console.warn(`⚠️ Could not fetch recent date for ${processCategory}`);
        }
      }

      // Update records with recent dates or fallback
      const updatedDetails = detailObj.map(item => {
        let lastWeldDate = detailDateFormat;
        
        if (item.WPQ_Process && item.WPQ_Type) {
          const category = ProcessActive.find(p => p.wld_code === item.WPQ_Process)?.wld_p_category;
          if (category) {
            const recentDate = recentDatesCache.get(`${category}#${item.WPQ_Type}`);
            if (recentDate) {
              // Compare dates and use the greater one
              const selectedDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              const [recentDay, recentMonth, recentYear] = recentDate.split("-");
              const recentDateObj = new Date(parseInt(recentYear), parseInt(recentMonth) - 1, parseInt(recentDay));
              
              // Use the greater date
              lastWeldDate = selectedDateObj >= recentDateObj ? detailDateFormat : recentDate;
              console.log(`📊 COMPARE: Process ${item.WPQ_Process} - Selected: ${detailDateFormat}, Recent: ${recentDate}, Using: ${lastWeldDate}`);
            }
          }
        }
        
        return {
          ...item,
          wld_createdon: lastWeldDate,
          WPQ_Welder_ExpiresOn: calculateWPQExpiryDate(lastWeldDate),
          wld_process_status: item.wld_process_status || "Active"
        };
      });

      setdetailObj(updatedDetails);
    };

    const validate = async () => {

      const validationResult = await validateWPQForm({
        headerObj,
        detailObj,
        WPStype,
        currentErrors: error,
        action,
        checkDuplicate,
      });

      setError((prevState) => ({
        ...prevState,
        ...validationResult.errors,
      }));

      return validationResult.isError;
    };

    const checkDuplicate = async (type, proj, revno) => {
      let result = await getbyrecordno(type, proj, revno);
      if (result[0] === undefined) return false;
      //Header exist check also if details exist
      let details = await getdetailsById(proj, revno);
      return details?.length > 0;
    };

    const validateTemp = async () => {
      let temperror = emptyError(error, setError, false);
      const validationResult = await validateWPQTempForm({
        headerObj,
        detailObj,
        currentErrors: error,
        action,
        checkDuplicate,
        WPStype,
      });

      setError((prevState) => ({
        ...prevState,
        ...validationResult.errors,
      }));

      return { isError: validationResult.isError, temperror };
    };

    const Processstatus = async (data) => {
      let Process_Status = [];
      let detail_SK = [];
      data.forEach((item) => {
        Process_Status.push(item.wld_process_status);
        detail_SK.push(item.WPQ_Process + "#" + item.WPQ_Type);
      });
      setheaderObj({ ...headerObj, Process_Status, detail_SK });
    };

    useEffect(() => {
      let WpqDetail = detailObj?.filter(
        (i) => i.WPQ_Process !== "NA" && i.WPQ_Process !== ""
      );
      if (WpqDetail.length > 0) {
        Processstatus(WpqDetail);
      }
    }, [detailObj?.map(item => `${item.WPQ_Process}#${item.WPQ_Type}#${item.wld_process_status}`).join('|')]);

    // RECORD CREATION: Header + Detail records, WQC fields for reports
    const handleAdd = async () => {
      let result = false;
      headerObj.WPQ_CompanyName = company_name[0]?.value;
      headerObj.WPQ_Last_UpdatedBy = username;
      if (
        headerObj?.WPSH_Status === Status.StatusApproved ||
        headerObj?.WPSH_Status === Status.StatusRejected
      ) {
        if (headerObj?.WPSH_Status === Status.StatusApproved)
          headerObj.WPS_Change_No = handleCount(headerObj.WPS_Change_No);
        headerObj.WPSH_Status = Status.StatusPending;
      }
      if (action === Action.ActionCopy) {
        headerObj.WPSH_Status = Status.StatusTempSaved;
        headerObj.WPSH_PreparedBy = sessionStorage.getItem("User");
      }
      const filteredDetails = detailObj.filter(
        (item) => item.WPQ_Process !== "NA" && item.WPQ_Process !== ""
      );
      // WQC FIELDS: Pipe-delimited strings for WCR reports

      let WQC_Process = "";
      let WQC_Type = "";
      let WQC_Pcat = "";

      headerObj.Process1 = filteredDetails[0]?.WPQ_Process || "";
      headerObj.Process2 = filteredDetails[1]?.WPQ_Process || "";
      headerObj.Process3 = filteredDetails[2]?.WPQ_Process || "";

      // Create process map for O(1) lookup
      const processMap = new Map(ProcessActive.map(p => [p.wld_code, p.wld_p_category]));

      // Build header process data
      const processes = filteredDetails.map(item => item.WPQ_Process);
      const types = filteredDetails.map(item => item.WPQ_Type);

      headerObj.WPSH_WPQ_Process = processes.join(",") + ",";
      headerObj.WPSH_WPQ_Type = types.join(",") + ",";

      // Process detail items efficiently
      for (const item of filteredDetails) {
        const category = processMap.get(item.WPQ_Process);
        if (!category) {
          console.error(`Process category not found for process: ${item.WPQ_Process}. Please select a valid process.`);
          showWarning(`Process category not found for process: ${item.WPQ_Process}. Please select a valid process.`);
          return MessageTypes.ErrorShown;
        }
        
        WQC_Process += item.WPQ_Process + "|";
        WQC_Type += item.WPQ_Type + "|";
        WQC_Pcat += category + "|";

        Object.assign(item, {
          WPQ_WelderId: headerObj.WPQ_WelderId,
          WPQ_RecordNo: headerObj.WPQ_RecordNo,
          wld_WPSType: headerObj.wld_WPSType,
          WPQ_WelderName: headerObj.WPQ_WelderName,
          WPQ_BM_WTBM_Pno: headerObj.WPQ_BM_WTBM_Pno,
          WPSH_Status: headerObj.WPSH_Status,
          PK: `${headerObj.WPQ_WelderId}#${headerObj.WPQ_RecordNo}`,
          SK: `${item.WPQ_Process}#${item.WPQ_Type}`,
          WPQ_Process_Category: `${category}#${item.WPQ_Type}`,
          WPSH_WPQ_Process: headerObj.WPSH_WPQ_Process,
          WPSH_WPQ_Type: headerObj.WPSH_WPQ_Type
        });
        if (item.wld_WPSType === "Groove") {
          item.WPQ_BMT_Plate_thickness_RangeQ_Overlay = parseFloat(headerObj.WPQ_BMT_Plate_thickness_RangeQ_Groove);
          item.WPQ_BMT_Pipe_Diameter_RangeQ_Overlay = parseFloat(headerObj.WPQ_BMT_Pipe_Diameter_RangeQ_Groove);
        } else {
          item.WPQ_BMT_Plate_thickness_RangeQ_Overlay = parseFloat(headerObj.WPQ_BMT_Plate_thickness_RangeQ_Overlay);
          item.WPQ_BMT_Pipe_Diameter_RangeQ_Overlay = parseFloat(headerObj.WPQ_BMT_Pipe_Diameter_RangeQ_Overlay);
        }
        item.WPQ_BM_WTBM_Pno = headerObj.WPQ_VAR_Pcategory_RangeQualified;
      }

      // Set WQC fields in headerObj for WCR compatibility
      headerObj.WQC_Process = WQC_Process;
      headerObj.WQC_Type = WQC_Type;
      headerObj.WQC_Pcat = WQC_Pcat;
      try {
        headerObj.WQC_Last_Weld_Date = filteredDetails.map(item => ({ Process: item.SK, LastWeldDate: item.wld_createdon }));

        let addresult = await AddNewGrooveWPQ(headerObj);
        if (addresult) {
          let oldProcess = await getdetailsById(
            headerObj.WPQ_WelderId,
            headerObj.WPQ_RecordNo
          );
          //delete old process
          for (const item of oldProcess) {
            let NotFoundprocess = filteredDetails.filter(
              (i) =>
                i.PK.toString() == item.PK.toString() &&
                i.SK.toString() == item.SK.toString()
            );
            if (NotFoundprocess && NotFoundprocess.length <= 0) {
              await DeleteWPQ(item.PK, item.SK);
            }
          }
          setAction(Action.ActionEdit);
          for (const item of filteredDetails) {
            try {
              let response = await AddNewDetail(item);
              result = response?.data?.message === "Success" || false;
            } catch (error) {
              console.error("Error adding detail:", error);
            }
          }
        }
        else{
          return MessageTypes.Fail;
        }
      } catch (error) {
        console.error("Error adding header:", error);
        return MessageTypes.SpecialCharacters;
      }
      return result;
    };

    const fetchProcess = async () => {
      const result = await getActiveProcess();
      const process = [{ wld_code: "NA" }, ...result];
      setProcessActive(process);
    };

    const fetchPNo = async () => {
      const result = await getPNOActive();
      setPNoActive(result);
    };

    const fetchWelder = async () => {
      const result = await getActiveWelder();
      const modifiedResult = result.map((welder) => ({
        ...welder,
        wld_salarycode: welder.wld_salarycode || welder.wld_code,
      }));
      setWelderActive(modifiedResult);
    };

    const fetchInspecAgency = async () => {
      const result = await getActiveInsp();
      setInspecAgencyActive(result);
    };

    useEffect(() => {
      fetchProcess();
      fetchPNo();
      fetchInspecAgency();
      fetchWelder();
    }, []);

    // Initialize dates for Add WPQ
    useEffect(() => {
      if (action === Action.ActionAdd && !headerObj?.WPQ_Date) {
        setheaderObj(prev => ({ ...prev, WPQ_Date: todayDate }));
      }
    }, [action]);

    // Watch for WPQ_Date changes and trigger sync
    useEffect(() => {
      if (headerObj?.WPQ_Date && detailObj.length > 0 && ProcessActive.length > 0) {
        console.log("🎯 TRIGGER: WPQ_Date changed via useEffect, initiating sync");
        console.log("📅 TRIGGER: Date value:", headerObj.WPQ_Date);
        console.log("📋 TRIGGER: DetailObj length:", detailObj.length);
        syncDatesWithWeldingDate(headerObj.WPQ_Date);
      }
    }, [headerObj?.WPQ_Date, ProcessActive]); // ✅ Added ProcessActive to dependencies
    
    // Apply recent dates only once when copy mode is initialized
    useEffect(() => {
      if (action === Action.ActionCopy && ProcessActive.length > 0 && detailObj.length > 0 && headerObj?.WPQ_Date) {
        const hasValidProcesses = detailObj.some(item => item.WPQ_Process && item.WPQ_Process !== "NA" && item.WPQ_Process !== "");
        if (hasValidProcesses) {
          console.log("🔄 COPY: Initial sync for copy mode");
          syncDatesWithWeldingDate(headerObj.WPQ_Date);
        }
      }
    }, [action, ProcessActive.length]); // Only run when action or ProcessActive changes



    const handleSubmit = () => handleWPQGeneralSubmit(
      validate,
      handleAdd,
      setLoading,
      setIsEdited,
      Status.StatusPending
    );

    const handleSubmitTemp = () => handleWPQGeneralSubmitTemp(
      IsEdited,
      validateTemp,
      handleAdd,
      setLoading,
      setIsEdited,
      headerObj?.WPQH_Status || Status.StatusTempSaved
    );

    useImperativeHandle(ref, () => ({
      funcOnNext: async () => {
        let res = false;
        res = await handleSubmit();
        return res;
      },

      funcOnNextTemp: async () => {
        let res = false;
        res = await handleSubmitTemp();
        return res;
      },
    }));

    return (
      <Row className="font">
        {loading && (
          <div className="loader-overlay">
            <DotLoader size={80} className="pageloader" />
          </div>
        )}
        <Row style={{ padding: "20px 60px" }} className="WPQ-Page-row-padding">
          <Row className="mb-2">
            <Col md={3}>
              <Form.Group className="pro-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WPQ_WelderId"
                    name="WPQ_WelderId"
                    className={error.WPQ_WelderId.status ? "err-input" : ""}
                    stylegreen={
                      error.WPQ_WelderId.status ? errorstyle : normalstyle
                    }
                    data={WelderActive}
                    valueField={"wld_salarycode"}
                    disabled={action === Action.ActionEdit}
                    value={headerObj?.WPQ_WelderId || ""}
                    labelField1={"wld_salarycode"}
                    labelField2={"wld_name"}
                    placeholder=""
                    onChange={(List) => {
                      setheaderObj((prev) => ({
                        ...prev,
                        WPQ_WelderId: List.value,
                      }));
                      setError((prevState) => ({
                        ...prevState,
                        WPQ_WelderId: {},
                      }));
                      let a = WelderActive.filter((b) => {
                        return b.wld_salarycode == List.value;
                      });
                      setheaderObj((prev) => ({
                        ...prev,
                        WPQ_WelderId: a[0].wld_salarycode,
                        WPQ_WelderName: a[0].wld_name,
                      }));
                      if (action === Action.ActionCopy) {
                        setReadonly(false);
                      }
                      setIsEdited(true);
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Welder Id</Form.Label>
                <div className="required-text-select">
                  {error.WPQ_WelderId.status && (
                    <Form.Text className="text-danger">
                      {error.WPQ_WelderId.message}
                    </Form.Text>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <div className="T-input w-100">
                  <input
                    type="text"
                    id="WPQ_WelderName"
                    name="WPQ_WelderName"
                    readOnly
                    placeholder=" "
                    disabled={action === Action.ActionEdit}
                    maxLength={20}
                    value={headerObj?.WPQ_WelderName}
                    onChange={(e) => {
                      handleChange(e);
                      if (action === Action.ActionCopy) {
                        setReadonly(false);
                      }
                    }}
                  />
                  <Form.Label htmlFor="WPQ_WelderName">Welder Name</Form.Label>
                </div>
              </Form.Group>
            </Col>
            <Col md={3} style={{ position: "relative", right: "3px" }}>
              <DateInput
                divClass="T-input w-100"
                label="Welding Date"
                className="groove_datecolor"
                name="WPQ_Date"
                todayDate={todayDate}
                maxDate={maxDate}
                readOnly={isReadonly}
                disabled={action === Action.ActionEdit && headerObj.WPSH_Status === Status.StatusApproved}
                obj={headerObj}
                setobj={setheaderObj}
                setError={setError}
                status={error.WPQ_Date.status}
                message={error.WPQ_Date.message}
              />
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  name="WPQ_RecordNo"
                  type="text"
                  maxLength={22}
                  status={error.WPQ_RecordNo.status}
                  disabled={action === Action.ActionEdit}
                  value={headerObj?.WPQ_RecordNo}
                  onChange={(e) => {
                    handleChange(e);
                    setIsEdited(true);
                    if (action === Action.ActionCopy) {
                      setReadonly(false);
                    }
                  }}
                  readOnly={isReadonly}
                  label="WPQ Record Number"
                  message={error.WPQ_RecordNo.message}
                  setError={setError}
                  error={error}
                  allowlistfunction={isAllowCustomSpecialCharacters}
                  allowcharacters="-"
                  customMessage="Special Characters # & % + $ ' < > = ; not allowed"
                ></SingleInput>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  name="WPQ_Wps_RecordNo"
                  type="text"
                  maxLength={20}
                  readOnly={isReadonly}
                  disabled={disableFields}
                  status={error.WPQ_Wps_RecordNo.status}
                  value={headerObj?.WPQ_Wps_RecordNo}
                  onChange={handleChange}
                  setError={setError}
                  error={error}
                  label="WPS Record Number"
                  message={error.WPQ_Wps_RecordNo.message}
                  allowlistfunction={isAllowCustomSpecialCharacters}
                  allowcharacters="-"
                  customMessage="Special Characters # & % + $ ' < > = ; not allowed"
                ></SingleInput>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  name="WPQ_ReviNo"
                  type="text"
                  maxLength={4}
                  status={error.WPQ_ReviNo.status}
                  readOnly={isReadonly}
                  disabled={disableFields}
                  value={headerObj?.WPQ_ReviNo}
                  onChange={handleChange}
                  setError={setError}
                  error={error}
                  label="Rev.No"
                  message={error.WPQ_ReviNo.message}
                ></SingleInput>
              </Form.Group>
            </Col>
            <Col md={3} className="mb-1">
              <Form.Group className="g-select">
                <Form.Select
                  id="WPQ_Inspection"
                  name="WPQ_Inspection"
                  type="text"
                  placeholder=" "
                  required
                  value={headerObj?.WPQ_Inspection}
                  onChange={(e) => {
                    setheaderObj((prev) => ({
                      ...prev,
                      WPQ_Inspection: e.target.value,
                    }));
                    setError((prevState) => ({
                      ...prevState,
                      WPQ_Inspection: {},
                    }));
                  }}
                  disabled={disableFields}
                  className={error.WPQ_Inspection.status ? "err-input" : ""}
                >
                  <option value="">Select</option>
                  {inspecAgencyActive?.map((item) => {
                    return (
                      <option key={item.wld_code} value={item.wld_code}>
                        {item.wld_code}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Label className="frm-label" htmlFor="WI_Joint_Type">
                  Inspection Agency
                </Form.Label>
              </Form.Group>
              <div className="required-text-select">
                {error.WPQ_Inspection.status && (
                  <Form.Text className="text-danger">
                    {error.WPQ_Inspection.message}
                  </Form.Text>
                )}
              </div>
            </Col>
            <Col md={3}>
              <DateInput
                divClass="T-input w-100"
                label="Date Of Birth"
                className="groove_datecolor"
                name="WPQ_DOB"
                todayDate={todayDate}
                maxDate={maxDate}
                disabled={disableFields}
                readOnly={isReadonly}
                obj={headerObj}
                setobj={setheaderObj}
                setError={setError}
                status={error.WPQ_DOB.status}
                message={error.WPQ_DOB.message}
              />
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  name="WPQ_Qualificationcode"
                  type="text"
                  maxLength={22}
                  readOnly={isReadonly}
                  status={error.WPQ_Qualificationcode.status}
                  value={headerObj?.WPQ_Qualificationcode}
                  onChange={handleChange}
                  setError={setError}
                  error={error}
                  label="Qualification Code"
                  message={error.WPQ_Qualificationcode.message}
                  disabled={disableFields}
                ></SingleInput>
              </Form.Group>
            </Col>
            <Col md={3}>
              <DateInput
                divClass="T-input w-100"
                label="Last Updated Date"
                className="groove_datecolor"
                name="WPQ_Last_UpdatedDate"
                todayDate={todayDate}
                maxDate={maxDate}
                disabled={disableFields}
                readOnly={isReadonly}
                obj={headerObj}
                setobj={setheaderObj}
                setError={setError}
                status={error.WPQ_Last_UpdatedDate.status}
                message={error.WPQ_Last_UpdatedDate.message}
              />
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  name="WPQ_Last_UpdatedBy"
                  type="text"
                  readOnly={isReadonly}
                  disabled={disableFields}
                  value={
                    action === Action.ActionEdit
                      ? headerObj?.WPQ_Last_UpdatedBy
                      : username
                  }
                  onChange={handleChange}
                  setError={setError}
                  error={error}
                  label="Last Updated By"
                ></SingleInput>
              </Form.Group>
            </Col>
            <Col md={3} className="mb-1">
              <Form.Group className="pro-input-select">
                <div className="search-select">
                  <SearchSelect
                    required
                    data={WeldedCoupon}
                    stylegreen={
                      error.WPQ_Welded_on_coupon.status
                        ? errorstyle
                        : normalstyle
                    }
                    readOnly={isReadonly}
                    disabled={disableFields}
                    valueField={"WPQ_Welded_on_coupon"}
                    value={headerObj?.WPQ_Welded_on_coupon}
                    labelField={"WPQ_Welded_on_coupon"}
                    placeholder=""
                    className={
                      error.WPQ_Welded_on_coupon.status ? "err-input" : ""
                    }
                    onChange={(pnoList) => {
                      setIsEdited(true);
                      setheaderObj((prev) => ({
                        ...prev,
                        WPQ_Welded_on_coupon: pnoList.value,
                      }));
                      setError((prevState) => ({
                        ...prevState,
                        WPQ_Welded_on_coupon: {},
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Welded On Coupon</Form.Label>
              </Form.Group>
              <div className="required-text-select">
                {error.WPQ_Welded_on_coupon.status && (
                  <Form.Text className="text-danger">
                    {error.WPQ_Welded_on_coupon.message}
                  </Form.Text>
                )}
              </div>
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  readOnly={isReadonly}
                  disabled={disableFields}
                  name="WPQ_CompanyName"
                  type="text"
                  value={company_name[0]?.value}
                  label="Company name"
                  setError={setError}
                  error={error}
                ></SingleInput>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <General_Single_Input_select
                  name="WPQ_Division"
                  type="text"
                  maxLength={20}
                  readOnly={isReadonly}
                  disabled={isReadonly}
                  status={error.WPQ_Division.status}
                  value={headerObj?.WPQ_Division}
                  handleSelectChange={handleChange}
                  label="Division"
                  message={error.WPQ_Division.message}
                  array={Division}
                ></General_Single_Input_select>
              </Form.Group>
            </Col>
            {/* <Col md={3}>
              <Form.Group className="form-switch-input">
                <Form.Label className="fieldLabel mb-0">Status</Form.Label>
                <Form.Switch
                  disabled={disableFields}
                  className={headerObj?.WPQ_Status ? "semiBoldToggleButton activeToggleLabel" : "semiBoldToggleButton inactiveToggleLabel"}
                  name="WPQ_Status"
                  checked={headerObj?.WPQ_Status}
                // onChange={(e) => {
                //   setheaderObj((prevState) => ({
                //     ...prevState,
                //     WPQ_Status: e.target.checked,
                //   }));
                //   setIsEdited(true);
                // }}
                />
                <span className={headerObj?.WPQ_Status ? "fieldLabel activeToggleLabel" : "fieldLabel inactiveToggleLabel"}>
                  {headerObj?.WPQ_Status ? "Active" : "Inactive"}
                </span>
              </Form.Group>
            </Col> */}
          </Row>
          <Row className="row-margin mt-1">
            <Col md={3}>
              <Form.Group>
                <General_Single_Input_select
                  name="WPQ_PlantLocation"
                  type="text"
                  maxLength={20}
                  readOnly={isReadonly}
                  disabled={disableFields}
                  status={error.WPQ_PlantLocation.status}
                  value={headerObj?.WPQ_PlantLocation}
                  handleSelectChange={handleChange}
                  label="Plant Location"
                  message={error.WPQ_PlantLocation.message}
                  array={PlantLocation}
                ></General_Single_Input_select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <SingleInput
                  name="WPQ_specialNote"
                  type="text"
                  maxLength={20}
                  readOnly={isReadonly}
                  disabled={disableFields}
                  status={error.WPQ_specialNote.status}
                  value={headerObj?.WPQ_specialNote}
                  onChange={handleChange}
                  setError={setError}
                  error={error}
                  label="Special Notes(for project to WPQ linking)"
                  message={error.WPQ_specialNote.message}
                ></SingleInput>
              </Form.Group>
            </Col>
          </Row>
        </Row>
        <Row style={{ background: "white", padding: "20px 60px" }}>
          <Row className="mb-1">
            <Col md={12}>
              <p
                className="WPS-blue-text "
                style={{ position: "relative", top: "10px" }}
              >
                Base Metal (QW-403){" "}
              </p>
              <BaseMetalTable
                headerObj={headerObj}
                setheaderObj={setheaderObj}
                error={error}
                isReadonly={isReadonly}
                handleChange={handleChange}
                setError={setError}
                PNoActive={PNoActive}
                blyestyle={blyestyle}
                errorstyle={errorstyle}
                disableFields={disableFields}
                setIsEdited={setIsEdited}
                isAllowDashedSpecialCharacters={isAllowDashedSpecialCharacters}
              ></BaseMetalTable>
            </Col>
          </Row>
          <Row className="mb-1">
            <Col md={12}>
              <p
                className="WPS-blue-text "
                style={{ position: "relative", top: "10px" }}
              >
                Variables{" "}
              </p>
              <WPQVariableTable
                style1={{ width: "0%" }}
                rowhead1="Actual Values"
                rowhead2="Range Qualified"
                colhead1="Type of Weld Joint"
                colhead2="Base Metal"
                colhead3="P Category (Category - P No.)"
                error={error}
                isReadonly={isReadonly}
                disableFields={disableFields}
                name1="WPQ_VAR_TypeofJoint_actualValue"
                name2="WPQ_VAR_TypeofJoint_RangeQualified"
                name3="WPQ_VAR_BaseM_actualValue"
                name4="WPQ_VAR_BaseM_RangeQualified"
                name6="WPQ_VAR_Pcategory_RangeQualified"
                value1={headerObj?.WPQ_VAR_TypeofJoint_actualValue}
                value2={headerObj?.WPQ_VAR_TypeofJoint_RangeQualified}
                value3={headerObj?.WPQ_VAR_BaseM_actualValue}
                value4={headerObj?.WPQ_VAR_BaseM_RangeQualified}
                value6={headerObj?.WPQ_VAR_Pcategory_RangeQualified}
                handleChange={handleChange}
                setError={setError}
                maxLength={35}
                PNoActive={PNoActive}
                setheaderObj={setheaderObj}
                setIsEdited={setIsEdited}
                isAllowDashedSpecialCharacters={isAllowDashedSpecialCharacters}
              ></WPQVariableTable>
            </Col>
          </Row>
        </Row>
        <Row style={{ padding: "20px 60px" }} className="WPQ-Page-row-padding">
          <Row className="mb-1">
            <Col md={12}>
              <p className="WPS-blue-text" style={{ position: "relative", top: "10px" }}>
                Base Metal Thickness
              </p>
              <WPQ_BaseMetalThichkness_Table
                headerObj={headerObj}
                error={error}
                isReadonly={isReadonly}
                handleChange={handleChange}
                setError={setError}
                disableFields={disableFields}
              ></WPQ_BaseMetalThichkness_Table>
            </Col>
          </Row>
        </Row>

        <Row style={{ background: "white", padding: "20px 60px" }}>
          <Row className="mb-1">
            <Col md={12}>
              <p
                className="WPS-blue-text "
                style={{ position: "relative", top: "10px" }}
              >
                Welding Process{" "}
              </p>
              <ProcessTypeTable
                detailObj={detailObj}
                setdetailObj={setdetailObj}
                ProcessActive={ProcessActive}
                fieldPrefix="WPQ"
                isReadonly={isReadonly}
                disableFields={disableFields}
                className="WPQSelect"
                customHeaderStyle='thicknessgreen_title'
              />
            </Col>
          </Row>
        </Row>
      </Row>
    );
  }
);

export default WPQ_General;